/**
 * Data Operations Utilities
 *
 * Pure functions for filtering, sorting, searching, and faceting data.
 * These functions are used by useClientDataControls but can also be used standalone.
 */

import Fuse from "fuse.js";
import type {
  FacetConfig,
  FacetCounts,
  FilterCondition,
  FilterGroup,
  FilterState,
  SearchConfig,
  SortState,
} from "../types/DataControls";

// ============================================================================
// Helper: Access nested object properties
// ============================================================================

/**
 * Get a nested property value from an object using dot notation
 * @example getNestedValue({ user: { name: 'Alice' } }, 'user.name') => 'Alice'
 */
export function getNestedValue(obj: unknown, path: string): unknown {
  return path.split(".").reduce((current, key) => (current as Record<string, unknown>)?.[key], obj);
}

// ============================================================================
// Filtering
// ============================================================================

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "string") return value.trim() === "";
  return false;
}

/**
 * Check if a single condition matches a data item
 */
function matchesCondition<T>(item: T, condition: FilterCondition): boolean {
  const value = getNestedValue(item, condition.field);
  const targetValue = condition.value;

  switch (condition.operator) {
    case "eq":
      return value === targetValue;

    case "neq":
      return value !== targetValue;

    case "in": {
      if (!Array.isArray(targetValue)) return false;
      // Array-valued fields (e.g. tags, manufacturerSlugs): treat as "any overlap".
      // A row matches if any of its values appear in the user's selection.
      if (Array.isArray(value)) {
        return (value as (string | number)[]).some((v) => targetValue.includes(v));
      }
      return targetValue.includes(value as string | number);
    }

    case "nin": {
      if (!Array.isArray(targetValue)) return false;
      // Array-valued fields: match rows whose values have NO overlap with the
      // user's selection. Empty array counts as "not in" — mirrors set semantics.
      if (Array.isArray(value)) {
        return !(value as (string | number)[]).some((v) => targetValue.includes(v));
      }
      return !targetValue.includes(value as string | number);
    }

    case "gt": {
      // Handle Date objects
      if (value instanceof Date && typeof targetValue === "string") {
        return value > new Date(targetValue);
      }
      return (value as number) > (targetValue as number);
    }

    case "gte": {
      // Handle Date objects
      if (value instanceof Date && typeof targetValue === "string") {
        return value >= new Date(targetValue);
      }
      return (value as number) >= (targetValue as number);
    }

    case "lt": {
      // Handle Date objects
      if (value instanceof Date && typeof targetValue === "string") {
        return value < new Date(targetValue);
      }
      return (value as number) < (targetValue as number);
    }

    case "lte": {
      // Handle Date objects
      if (value instanceof Date && typeof targetValue === "string") {
        return value <= new Date(targetValue);
      }
      return (value as number) <= (targetValue as number);
    }

    case "contains":
      if (typeof value !== "string") return false;
      return value.toLowerCase().includes(String(targetValue).toLowerCase());

    case "startsWith":
      if (typeof value !== "string") return false;
      return value.toLowerCase().startsWith(String(targetValue).toLowerCase());

    case "endsWith":
      if (typeof value !== "string") return false;
      return value.toLowerCase().endsWith(String(targetValue).toLowerCase());

    case "isEmpty":
      return isEmptyValue(value);

    case "isNotEmpty":
      return !isEmptyValue(value);

    default:
      return false;
  }
}

/**
 * Check if a filter group matches a data item
 */
function matchesGroup<T>(item: T, group: FilterGroup): boolean {
  if (group.conditions.length === 0) return true;

  const results = group.conditions.map((condition) => {
    if ("conditions" in condition) {
      // Nested group
      return matchesGroup(item, condition);
    }
    // Single condition
    return matchesCondition(item, condition);
  });

  return group.logic === "AND" ? results.every(Boolean) : results.some(Boolean);
}

/**
 * Apply filters to a dataset
 */
export function applyFilters<T>(data: T[], filters: FilterState): T[] {
  if (!filters || filters.conditions.length === 0) {
    return data;
  }

  return data.filter((item) => matchesGroup(item, filters));
}

// ============================================================================
// Sorting
// ============================================================================

/**
 * Compare two values for sorting
 */
function compareValues(a: unknown, b: unknown, direction: "asc" | "desc"): number {
  // Handle null/undefined
  if (a == null && b == null) return 0;
  if (a == null) return direction === "asc" ? 1 : -1;
  if (b == null) return direction === "asc" ? -1 : 1;

  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    const diff = a.getTime() - b.getTime();
    return direction === "asc" ? diff : -diff;
  }

  // Handle strings (case-insensitive)
  if (typeof a === "string" && typeof b === "string") {
    const comparison = a.toLowerCase().localeCompare(b.toLowerCase());
    return direction === "asc" ? comparison : -comparison;
  }

  // Handle numbers and other types
  if (a < b) return direction === "asc" ? -1 : 1;
  if (a > b) return direction === "asc" ? 1 : -1;
  return 0;
}

/**
 * Apply sorting to a dataset
 */
export function applySort<T>(data: T[], sort: SortState | null): T[] {
  if (!sort) {
    return data;
  }

  return [...data].sort((a, b) => {
    const aValue = getNestedValue(a, sort.field);
    const bValue = getNestedValue(b, sort.field);
    return compareValues(aValue, bValue, sort.direction);
  });
}

// ============================================================================
// Search
// ============================================================================

/**
 * Apply search to a dataset
 */
export function applySearch<T>(data: T[], search: SearchConfig | null): T[] {
  if (!search || !search.query.trim()) {
    return data;
  }

  const { query, fields, fuzzy = true, threshold = 0.3 } = search;

  // Use Fuse.js for fuzzy search
  if (fuzzy) {
    const fuse = new Fuse(data, {
      keys: fields,
      threshold,
      ignoreLocation: true,
      includeScore: true,
    });

    const results = fuse.search(query);
    return results.map((result) => result.item);
  }

  // Exact search (fallback)
  const lowerQuery = query.toLowerCase();
  return data.filter((item) => {
    return fields.some((field) => {
      const value = getNestedValue(item, field);
      if (value == null) return false;
      return String(value).toLowerCase().includes(lowerQuery);
    });
  });
}

// ============================================================================
// Faceting
// ============================================================================

/**
 * Compute facet counts from a dataset
 */
export function computeFacets<T>(data: T[], facetConfigs: FacetConfig[]): FacetCounts {
  const counts: FacetCounts = {};

  for (const config of facetConfigs) {
    counts[config.field] = {};

    // If predefined values are provided, initialize with 0 counts
    if (config.values) {
      for (const valueConfig of config.values) {
        counts[config.field][String(valueConfig.value)] = 0;
      }
    }

    // Count occurrences in the data
    for (const item of data) {
      const value = getNestedValue(item, config.field);

      if (value == null) continue;

      // Handle arrays (multi-value fields)
      if (Array.isArray(value)) {
        for (const v of value) {
          const key = String(v);
          counts[config.field][key] = (counts[config.field][key] || 0) + 1;
        }
      } else {
        const key = String(value);
        counts[config.field][key] = (counts[config.field][key] || 0) + 1;
      }
    }
  }

  return counts;
}

// ============================================================================
// Combined Operations
// ============================================================================

export interface ApplyDataOperationsOptions<T> {
  data: T[];
  filters?: FilterState;
  sort?: SortState | null;
  search?: SearchConfig | null;
  facetConfigs?: FacetConfig[];
}

export interface ApplyDataOperationsResult<T> {
  data: T[];
  facetCounts: FacetCounts;
  resultCount: number;
}

/**
 * Apply all data operations in the correct order:
 * 1. Search
 * 2. Filter
 * 3. Compute facets (on filtered data)
 * 4. Sort
 */
export function applyDataOperations<T>({
  data,
  filters,
  sort,
  search,
  facetConfigs = [],
}: ApplyDataOperationsOptions<T>): ApplyDataOperationsResult<T> {
  // 1. Apply search
  let processedData = applySearch(data, search || null);

  // 2. Apply filters
  processedData = applyFilters(processedData, filters || null);

  // 3. Compute facets on filtered data (before sorting)
  const facetCounts = computeFacets(processedData, facetConfigs);

  // 4. Apply sorting
  processedData = applySort(processedData, sort || null);

  return {
    data: processedData,
    facetCounts,
    resultCount: processedData.length,
  };
}
